import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StudentFlagDef } from '@td/shared';
import { get, post, patch, del } from '../lib/api';

const KEY = ['student-flags'];

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: KEY });
  // Deleting a flag pulls it off every student, so the roster is stale too.
  void qc.invalidateQueries({ queryKey: ['students'] });
}

export function useStudentFlags() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<StudentFlagDef[]>('/student-flags'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; short?: string; color: string }) =>
      post<StudentFlagDef>('/student-flags', input),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      ...input
    }: {
      key: string;
      label?: string;
      short?: string;
      color?: string;
    }) => patch<StudentFlagDef>(`/student-flags/${key}`, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      del<{ clearedFrom: number; canRestore: boolean }>(`/student-flags/${key}`),
    onSuccess: () => invalidate(qc),
  });
}

export function useRestoreFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => post<StudentFlagDef>(`/student-flags/${key}/restore`),
    onSuccess: () => invalidate(qc),
  });
}
